import {
  DataGrid,
  DataGridProps,
  GridActionsCell,
  GridActionsCellItem,
  GridColDef,
  GridColumnHeaderParams,
  GridLocaleText,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
  GridTreeNodeWithRender,
} from "@mui/x-data-grid";
import {BaseEntityForm, EntityField} from "../../../core/model/BaseForm";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  SvgIconOwnProps,
  SxProps,
  Theme,
} from "@mui/material";
import {ReactNode, useEffect, useState} from "react";
import CrudForm, {FormLabel} from "../Form";
import {BaseEntity} from "../../../core/model/BaseEntity";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import ConfirmDialog, {ConfirmDialogLabel} from "../../confirm-dialog";
import {translate} from "@util";
import {Pageable} from "../../../core/model/Response";
import {useThemeContext} from "@theme";
import {InputSearch} from "@components/input-search";
import {useDownBreakpoint} from "@hooks";
import {EmptyData} from "@components/EmptyData";
import {Paginator} from "@components";
import {entity} from "@core/model";

export type TableLabel = GridLocaleText & {
  delete: string;
  deleteRows: string;
  add: string;
  edit: string;
  close: string;
  refresh: string;
};

type Props<T extends BaseEntity> = {
  entity: entity;
  sx?: SxProps<Theme>;
  topLeft?: ReactNode;
  hideSearch?: boolean;
  EntityIcon?: React.ComponentType<SvgIconOwnProps>;
  readOnly?: boolean;
  newEntityData?: Partial<T>;
  editEntityData?: Partial<T>;
  deleteDialogLabel?: Partial<ConfirmDialogLabel>;
  tableLabel?: Partial<TableLabel>;
  formLabel?: Partial<FormLabel>;
  form: BaseEntityForm;
  datagridProps?: Partial<DataGridProps>;
  data?: Pageable<T>;
  isDataGridLoading?: boolean;
  isFormLoading?: boolean;
  isFormFetching?: boolean;
  isDeleteLoading?: boolean;
  selectValues?: {[key: string]: {id: string | number; name: string}[]};
  CustomFormButton?: ReactNode;
  customActions?: {
    renderer: (
      params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
    ) => ReactNode | ReactNode[];
    length: number;
  };
  onSearch: (text: string) => void;
  onClear: () => void;
  onFieldChange?: (field: string, value: any) => void;
  onPaginationSortChange?: (
    pagination: GridPaginationModel,
    sort: GridSortModel,
  ) => void;
  onRefresh: () => void;
  onRowClick?: (entity: T) => void;
  onSave: (entity: T) => void;
  onEdit: (id: string | undefined) => void;
  onDelete: (entities: T[]) => void;
};

export default function CrudTable<T extends BaseEntity>(props: Props<T>) {
  const {
    entity,
    EntityIcon,
    topLeft,
    readOnly,
    newEntityData,
    editEntityData,
    form,
    datagridProps,
    data,
    isDataGridLoading,
    isFormLoading,
    isFormFetching,
    isDeleteLoading,
    selectValues,
    hideSearch,
    formLabel,
    tableLabel = {
      delete: translate("common.table.delete"),
      deleteRows: translate("common.table.deleteRows"),
      add: translate("common.table.add"),
      edit: translate("common.table.edit"),
      close: translate("common.table.close"),
      refresh: translate("common.table.refresh"),
      noRowsLabel: translate("common.table.noRowsLabel"),
      paginationRowsPerPage: translate("paginator.rowsPerPage"),
      paginationDisplayedRows: parmas =>
        `${parmas.from}-${parmas.to} ${translate("paginator.of")} ${parmas.count}`,
    },
    deleteDialogLabel = {
      title: translate("deleteDialog.title"),
      cancel: translate("deleteDialog.cancel"),
      confirm: translate("deleteDialog.confirm"),
      text: translate("deleteDialog.text"),
    },
    CustomFormButton,
    customActions,
    onSearch,
    onClear,
    onFieldChange,
    onRefresh,
    onPaginationSortChange,
    onRowClick,
    onEdit,
    onSave,
    onDelete,
  } = props;

  const {theme} = useThemeContext();
  const isDownLg = useDownBreakpoint("lg");
  const isDownSm = useDownBreakpoint("sm");

  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [openConfirmDelete, setOpenConfirmDelete] = useState<boolean>(false);
  const [rowToDelete, setRowToDelete] = useState<T>();
  const [selected, setSelected] = useState<T[]>([]);
  const [editEntity, setEditEntity] = useState<Partial<T>>();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>();

  useEffect(() => {
    setEditEntity(editEntityData);
    setOpenEditDialog(!!editEntityData);
  }, [editEntityData, setEditEntity]);

  const actionsCount = readOnly
    ? (customActions?.length ?? 0)
    : (customActions?.length ?? 0) + 2;

  const columns: (GridColDef | EntityField)[] =
    actionsCount > 0
      ? [
          ...form.columns,
          {
            field: "actions",
            width: (isDownLg ? 30 : 50) * actionsCount,
            type: "actions",
            renderCell: params => (
              <GridActionsCell {...params}>
                {customActions && customActions.renderer(params)}
                {!readOnly && (
                  <>
                    <GridActionsCellItem
                      showInMenu={isDownLg}
                      icon={<EditIcon color="primary" fontSize="small" />}
                      onClick={evt => handleEdit(evt, params.row)}
                      label={tableLabel.edit ?? "Edit"}
                    />
                    <GridActionsCellItem
                      showInMenu={isDownLg}
                      icon={<DeleteIcon color="error" fontSize="small" />}
                      onClick={evt => handleRowDelete(evt, params.row)}
                      label={tableLabel.delete ?? "Delete"}
                    />
                  </>
                )}
              </GridActionsCell>
            ),
          },
        ]
      : form.columns;

  const translateHeader = (params: GridColumnHeaderParams) =>
    params.colDef.headerName ? translate(params.colDef.headerName) : undefined;
  columns.forEach(
    c => (c.renderHeader = c.renderHeader ? c.renderHeader : translateHeader),
  );

  const handleNewClick = () => {
    setEditEntity(newEntityData ?? ({} as T));
    onEdit(undefined);
    setOpenEditDialog(true);
  };

  const handleEdit = (
    evt: React.MouseEvent<HTMLElement, MouseEvent>,
    row: GridRowParams,
  ) => {
    evt.stopPropagation();
    onEdit(row.id as string);
    setOpenEditDialog(true);
  };

  const handleCloseForm = () => {
    setEditEntity(undefined);
    onEdit(undefined);
    setOpenEditDialog(false);
  };

  const handleDelete = () => {
    setOpenConfirmDelete(false);
    if (onDelete && rowToDelete) {
      onDelete([rowToDelete]);
    } else if (onDelete && selected) {
      onDelete(selected);
    }
  };

  const handleRowDelete = (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    entity: T,
  ) => {
    e.stopPropagation();
    setRowToDelete(entity);
    setOpenConfirmDelete(true);
  };

  const closeConfirmDelete = () => {
    setRowToDelete(undefined);
    setOpenConfirmDelete(false);
  };

  const handleRowClick = (row: GridRowParams) => {
    if (onRowClick) {
      onRowClick(row.row as T);
    }
  };

  const handleSave = (data: T) => {
    if (onSave) {
      onSave(data);
    }
    handleCloseForm();
  };

  const handlePaginationSortChange = (
    pagination?: GridPaginationModel,
    sort?: GridSortModel,
  ) => {
    setPaginationModel(pagination ?? paginationModel);
    setSortModel(sort ?? sortModel);
    if (onPaginationSortChange) {
      onPaginationSortChange(
        pagination ?? paginationModel,
        sort ?? sortModel ?? [],
      );
    }
  };

  const handleRowSelection = (selection: GridRowSelectionModel) => {
    const selectedRows = data?.content.filter(r => {
      return r.id && selection.ids.has(r.id);
    });
    setSelected(selectedRows ?? []);
  };

  const columnVisibilityModel: {[key: string]: boolean} = {};
  form.columns.forEach(col => {
    columnVisibilityModel[col.field] =
      col.hideColumn === undefined ? true : !col.hideColumn;
  });

  const dialogTitle = editEntity?.id
    ? `${formLabel?.edit ?? "Edit"} ${formLabel?.entity ?? ""}`
    : `${formLabel?.new ?? "New"} ${formLabel?.entity ?? ""}`;

  return (
    <>
      <Stack
        className="crud-table-actions"
        direction="row"
        gap={1}
        px={2}
        py={isDownSm ? 1 : 2}
        alignItems="center">
        {topLeft}
        {!hideSearch && (
          <InputSearch
            entity={entity}
            containerStyle={{mr: "auto"}}
            onSearch={onSearch}
            onClear={onClear}
          />
        )}
        {onRefresh && (
          <Button
            sx={{
              minWidth: 40,
              maxWidth: 40,
              height: 40,
              p: 1,
              borderRadius: 8,
              "& .MuiButton-icon": {m: 0},
            }}
            size="medium"
            variant="text"
            onClick={onRefresh}
            loading={isDataGridLoading}>
            <RefreshIcon color="action" fontSize="medium" />
          </Button>
        )}
        {!readOnly && !isDownSm && (
          <Button
            variant="contained"
            startIcon={<AddIcon fontSize="inherit" />}
            onClick={handleNewClick}>
            {formLabel?.new}
          </Button>
        )}
        {!readOnly && isDownSm && (
          <IconButton onClick={handleNewClick}>
            <AddIcon fontSize="inherit" />
          </IconButton>
        )}
      </Stack>

      {/* Data table */}
      <Stack
        sx={{
          flex: "1 1 0",
          overflow: "hidden",
        }}>
        <DataGrid
          {...datagridProps}
          sx={{
            border: "none",
            height: "100%",
            "& .MuiDataGrid-columnHeader": {textTransform: "capitalize"},
            "& .MuiDataGrid-virtualScroller": {flex: "1 1 0"},
            "& .MuiDataGrid-menu": {
              backgroundColor: "red",
            },
          }}
          loading={isDataGridLoading || isFormLoading}
          rows={data?.content ?? []}
          rowCount={data?.totalElements ?? 0}
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          paginationModel={paginationModel}
          paginationMode={"server"}
          pageSizeOptions={[20, 50, 100]}
          onPaginationModelChange={(pagination: GridPaginationModel) =>
            handlePaginationSortChange(pagination, undefined)
          }
          onSortModelChange={(sort: GridSortModel) =>
            handlePaginationSortChange(undefined, sort)
          }
          sortModel={sortModel}
          sortingMode={onPaginationSortChange ? "server" : "client"}
          onRowClick={e => handleRowClick(e.row)}
          onRowSelectionModelChange={handleRowSelection}
          localeText={tableLabel}
          slots={{
            noRowsOverlay: () => (
              <Stack height={"100%"}>
                <EmptyData Icon={EntityIcon} />
              </Stack>
            ),
            basePagination: Paginator,
          }}
          slotProps={{
            loadingOverlay: {
              variant: "linear-progress",
              noRowsVariant: "skeleton",
            },
          }}
        />
      </Stack>

      {selected.length > 0 && !readOnly && (
        <Box position={"absolute"} bottom={16} left={16}>
          <Button
            className="crud-table-delete"
            loading={isDeleteLoading}
            variant="contained"
            color="error"
            onClick={() => setOpenConfirmDelete(true)}
            endIcon={<DeleteIcon fontSize="small" />}>
            {tableLabel.deleteRows?.replace(
              "%length%",
              selected.length.toString(),
            ) ?? `Delete ${selected} rows`}
          </Button>
        </Box>
      )}

      {/* Edit form */}
      <Dialog open={openEditDialog} onClose={handleCloseForm} fullWidth>
        <DialogContent>
          <Box
            sx={{
              bgcolor: theme.palette.secondary.dark,
              width: "64px",
              borderRadius: "32px",
              height: "64px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              margin: "0 auto",
            }}>
            {EntityIcon ? (
              <EntityIcon
                fontSize="large"
                sx={{color: theme.palette.text.primary}}
              />
            ) : (
              <></>
            )}
          </Box>
          <DialogTitle align="center">{dialogTitle}</DialogTitle>

          <CrudForm<T>
            label={formLabel}
            form={form}
            data={editEntity ?? ({} as T)}
            isLoading={isFormLoading}
            isFetching={isFormFetching}
            selectValues={selectValues}
            CustomButton={CustomFormButton}
            onFieldChange={onFieldChange}
            onSave={handleSave}
            onReset={() => setSelected([])}
          />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        Icon={DeleteIcon}
        open={openConfirmDelete}
        label={deleteDialogLabel}
        onConfirm={() => handleDelete()}
        onCancel={() => closeConfirmDelete()}
      />
    </>
  );
}
